import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  ChevronUp,
  ChevronDown,
  Search,
  Copy,
  RotateCcw,
  Loader2,
  UserCheck,
  AlertTriangle,
  Info,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "../../../components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Devolution } from "@/types/devolution";
import {
  getDevolutions,
  deleteDevolution,
  updateDevolutionStatus,
  getShowDevolution,
  syncDevolutionBtgStatus,
  preparePixDevolution,
  cancelPixDevolution,
} from "@/Api/devolution";
import { formatDate } from "@/utils/format-date";
import { Mask } from "@/utils/mask";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { AddDevolution } from "../Add";
import { Main } from "@/components/Main";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ApiDataProps } from "@/pages/Tenants";
import { getPixOperatorConfig } from "@/Api/configTenant";
import { getBankName } from "@/utils/banks";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TypePageActions } from "@/pages/Plans";
import { ScrollArea } from "@/components/ui/scroll-area";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { ShowBtn } from "@/components/Show";
import {
  DevolutionsFiltersComponent,
  DevolutionsFilters,
} from "@/components/DevolutionsFilters";
import { TypeUser } from "@/types/userType";
import { PERMISSIONS } from "@/constants/permissions";
import { ReceiptModal } from "../ReceiptModal";

const DEVOLUTION_STATUS = {
  PENDING: 1,
  APPROVED: 2,
  REJECTED: 3,
} as const;

const getStatusColor = (statusId: number): string => {
  switch (statusId) {
    case DEVOLUTION_STATUS.PENDING:
      return "bg-yellow-500 text-white border-yellow-500";
    case DEVOLUTION_STATUS.APPROVED:
      return "bg-green-500 text-white border-green-500";
    case DEVOLUTION_STATUS.REJECTED:
      return "bg-red-500 text-white border-red-500";
    default:
      return "bg-gray-500 text-white border-gray-500";
  }
};

const getPixKeyType = (key: string): string => {
  const value = (key || "").trim();
  const digits = value.replace(/\D/g, "");
  if (value.includes("@")) return "E-mail";
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value))
    return "Chave aleatória";
  if (value.startsWith("+")) return "Telefone";
  if (/^[\d.\-\/]+$/.test(value)) {
    if (digits.length === 14) return "CNPJ";
    if (digits.length === 11) return "CPF ou telefone";
  }
  return "Chave PIX";
};

const formatDocument = (doc?: string): string => {
  const d = (doc || "").replace(/\D/g, "");
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (d.length === 14)
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return doc || "Não informado";
};

const BTG_FAILED_STATUSES = ["FAILED", "CANCELED", "REVERTED"];

const getBtgKeyTypeLabel = (type: string): string =>
  ({
    PHONE: "Telefone",
    EMAIL: "E-mail",
    CPF: "CPF",
    CNPJ: "CNPJ",
    TAX_ID: "CPF/CNPJ",
    EVP: "Chave aleatória",
  } as Record<string, string>)[type] ?? type;

type BtgPixLabel = {
  /** Texto completo (mostrado nos detalhes) */
  text: string;
  /** Texto curto, usado dentro do badge */
  short: string;
  /** Cor do texto nos detalhes */
  className: string;
  /** Cor do badge */
  badgeClassName: string;
};

const BTG_TONE = {
  green: { className: "text-green-700", badge: "bg-green-500 text-white border-green-500" },
  amber: { className: "text-amber-700", badge: "bg-amber-500 text-white border-amber-500" },
  red: { className: "text-red-700", badge: "bg-red-500 text-white border-red-500" },
} as const;

const getBtgPixLabel = (
  btgStatus?: string | null,
  needsApproval?: boolean | null,
  awaitingConfirmation?: boolean
): BtgPixLabel | null => {
  const make = (
    tone: keyof typeof BTG_TONE,
    text: string,
    short: string
  ): BtgPixLabel => ({
    text,
    short,
    className: BTG_TONE[tone].className,
    badgeClassName: BTG_TONE[tone].badge,
  });

  if (awaitingConfirmation) {
    return make(
      "amber",
      "PIX criado no BTG, aguardando a sua confirmação",
      "PIX aguardando confirmação"
    );
  }

  if (!btgStatus) return null;

  if (BTG_FAILED_STATUSES.includes(btgStatus)) {
    return make("red", "PIX não concluído, aprove novamente", "PIX não concluído");
  }

  // Na prática o PIX já saiu e o cliente já recebeu quando o BTG chega em CONFIRMED
  if (btgStatus === "PROCESSED" || btgStatus === "CONFIRMED") {
    return make("green", "PIX enviado", "PIX enviado");
  }

  if ((btgStatus === "STARTED" || btgStatus === "CREATED") && needsApproval) {
    return make(
      "amber",
      "PIX aguardando aprovação do representante no BTG",
      "aguardando BTG"
    );
  }

  return make("amber", "PIX em processamento", "PIX em processamento");
};

interface TableDataProps extends Devolution {}

interface ExtendedPageActions extends TypePageActions {
  refresh?: () => Promise<void>;
  goToPage?: (page: number) => Promise<void>;
}

interface TableDataExtendedProps {
  onSort?: (column: string, direction: "asc" | "desc") => void;
  sortConfig?: {
    column: string;
    direction: "asc" | "desc";
  };
}

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
  onSort,
  sortConfig,
}: {
  dataApi: ApiDataProps<TableDataProps>;
  pageActions: ExtendedPageActions;
  updateFront: any;
} & TableDataExtendedProps) => {
  const { hasPermission, user } = usePermissions();

  // Envio de comprovante: Super Admin ou usuários (admin do tenant / responsável) com a permissão.
  // Espelha a regra do backend; o hasPermission libera por padrão outros tipos, como o vendedor.
  const canManageReceipts =
    user?.user_type_id === TypeUser.admin ||
    ((user?.user_type_id === TypeUser.client ||
      user?.user_type_id === TypeUser.unity_responsible) &&
      hasPermission(PERMISSIONS.UPLOAD_DEVOLUTION_RECEIPT));

  const updateFilesCount = (uuid: string, count: number) => {
    updateFront((prev: any) => ({
      ...prev,
      data: prev?.data?.map((row: TableDataProps) =>
        row?.uuid === uuid ? { ...row, files_count: count } : row
      ),
    }));
  };
  const [statusModal, setStatusModal] = useState<{
    isOpen: boolean;
    devolution: TableDataProps | null;
    newStatusId: number;
  }>({
    isOpen: false,
    devolution: null,
    newStatusId: 0,
  });

  const [observation, setObservation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Erro da última tentativa de aprovar/rejeitar, mostrado dentro do modal
  const [statusError, setStatusError] = useState<string | null>(null);
  const [syncingUuid, setSyncingUuid] = useState<string | null>(null);

  // PIX automático só quando a Operadora de Pagamentos / Transferência PIX for o BTG (Sicredi desativado)
  const [autoPix, setAutoPix] = useState(false);

  useEffect(() => {
    getPixOperatorConfig().then(
      ({ pixPayoutBtgActive, pixPayoutSicrediActive }) =>
        setAutoPix(pixPayoutBtgActive && !pixPayoutSicrediActive)
    );
  }, []);

  // Etapa 1 da aprovação com PIX automático: iniciação criada no BTG e titular da chave consultado
  const [review, setReview] = useState<{
    loading: boolean;
    devolution: TableDataProps | null;
  }>({ loading: false, devolution: null });
  const [ackMismatch, setAckMismatch] = useState(false);

  const pushRow = (updated: Partial<TableDataProps> & { uuid: string }) => {
    updateFront((prev: any) => ({
      ...prev,
      data: prev?.data?.map((row: TableDataProps) =>
        row?.uuid === updated.uuid ? { ...row, ...updated } : row
      ),
    }));
  };

  useEffect(() => {
    const base = statusModal.devolution;
    if (
      !statusModal.isOpen ||
      statusModal.newStatusId !== DEVOLUTION_STATUS.APPROVED ||
      !autoPix ||
      !base
    ) {
      return;
    }

    let cancelled = false;
    setReview({ loading: true, devolution: base });
    setStatusError(null);
    setAckMismatch(false);

    const run = async () => {
      let current: any = base;

      if (!base.btg_batch_id) {
        const res: any = await preparePixDevolution(base.uuid);
        if (cancelled) return;
        if (!res?.ok) {
          setStatusError(
            res?.response?.data?.message || "Não foi possível consultar o BTG."
          );
          setReview({ loading: false, devolution: base });
          return;
        }
        current = { ...base, ...res.data.response };
        pushRow(current);
      }

      // O BTG pode demorar alguns segundos para informar o titular da chave
      for (let i = 0; i < 10 && current.btg_batch_id && !current.btg_receiver_name; i++) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        if (cancelled) return;
        const res: any = await syncDevolutionBtgStatus(base.uuid);
        if (!res?.ok) break;
        current = { ...current, ...res.data.response };
        pushRow(current);
      }

      if (cancelled) return;

      if (!current.btg_batch_id) {
        setStatusError(
          "O BTG não manteve o pagamento (cancelado ou recusado). Feche e tente aprovar de novo."
        );
      } else if (!current.btg_receiver_name) {
        setStatusError(
          "O BTG ainda não informou o titular da chave. Feche e abra a aprovação novamente em instantes."
        );
      }

      setReview({ loading: false, devolution: current });
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    statusModal.isOpen,
    statusModal.devolution?.uuid,
    statusModal.newStatusId,
    autoPix,
  ]);

  // Cancela no BTG a iniciação criada na etapa 1; a devolução continua pendente
  const handleCancelPix = async () => {
    const current = review.devolution;
    if (!current || isSubmitting) return;

    setIsSubmitting(true);
    setStatusError(null);

    try {
      const res: any = await cancelPixDevolution(current.uuid);

      if (!res?.ok) {
        setStatusError(
          res?.response?.data?.message || "Não foi possível cancelar o PIX."
        );
        return;
      }

      pushRow(res.data.response);
      toast.success("PIX cancelado no BTG. A devolução continua pendente.");
      setStatusModal({ isOpen: false, devolution: null, newStatusId: 0 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSyncBtgStatus = async (uuid: string) => {
    setSyncingUuid(uuid);
    try {
      const res = await syncDevolutionBtgStatus(uuid);
      const updated = res?.data?.response;

      if (updated) {
        updateFront((prev: any) => ({
          ...prev,
          data: prev?.data?.map((row: TableDataProps) =>
            row?.uuid === uuid ? { ...row, ...updated } : row
          ),
        }));
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Erro ao consultar o status do PIX"
      );
    } finally {
      setSyncingUuid(null);
    }
  };


  const handleStatusChange = async () => {
    if (!statusModal.devolution || isSubmitting) return;

    setIsSubmitting(true);
    setStatusError(null);

    try {
      const res = await updateDevolutionStatus(statusModal.devolution.uuid, {
        devolution_status_id: statusModal.newStatusId,
        observation: observation.trim() || undefined,
      });

      // O interceptor do axios já avisa o erro com um toast e não lança exceção:
      // sem esta conferência a tela mostraria sucesso e fecharia o modal
      if (!res?.ok) {
        setStatusError(
          (res as any)?.response?.data?.message ||
            "Não foi possível concluir. Tente novamente."
        );
        return;
      }

      const pixSent = !!res?.data?.response?.btg_sent_at;

      toast.success(
        pixSent
          ? "Devolução aprovada. O PIX sai após a aprovação no BTG."
          : "Status da devolução atualizado com sucesso!"
      );

      if (pageActions.refresh) {
        await pageActions.refresh();
      }

      setStatusModal({ isOpen: false, devolution: null, newStatusId: 0 });
      setObservation("");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Erro ao atualizar status da devolução"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSort = (column: string) => {
    if (!onSort) return;

    const direction =
      sortConfig?.column === column && sortConfig?.direction === "asc"
        ? "desc"
        : "asc";

    onSort(column, direction);
  };

  const getSortIcon = (column: string) => {
    if (sortConfig?.column !== column) {
      return null;
    }
    return sortConfig?.direction === "asc" ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  const openStatusModal = (devolution: TableDataProps, newStatusId: number) => {
    setStatusModal({
      isOpen: true,
      devolution,
      newStatusId,
    });
    setObservation("");
    setStatusError(null);
  };

  const getStatusText = (statusId: number): string => {
    switch (statusId) {
      case DEVOLUTION_STATUS.APPROVED:
        return "Aprovar";
      case DEVOLUTION_STATUS.REJECTED:
        return "Rejeitar";
      default:
        return "Alterar Status";
    }
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort("client_name")}
            >
              <div className="flex items-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                Cliente{getSortIcon("client_name")}
              </div>
            </TableHead>
            <TableHead>Chave PIX</TableHead>
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 max-w-[150px]"
              onClick={() => handleSort("name")}
            >
              <div className="flex items-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                Descrição{getSortIcon("name")}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort("value")}
            >
              <div className="flex items-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                Valor{getSortIcon("value")}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort("devolution_status_id")}
            >
              <div className="flex items-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                Status{getSortIcon("devolution_status_id")}
              </div>
            </TableHead>
            <TableHead>Criado por</TableHead>
            <TableHead
              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleSort("created_at")}
            >
              <div className="flex items-center gap-1">
                <Search className="h-3 w-3 text-gray-400" />
                Data{getSortIcon("created_at")}
              </div>
            </TableHead>
            <TableHead className="text-center min-w-[180px]">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data.map((item) => (
            <TableRow key={item?.id}>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium">{item?.client?.name}</span>
                  <span className="text-sm text-muted-foreground">
                    {item?.client?.email}
                  </span>
                </div>
              </TableCell>
                            <TableCell>
                {item?.pix_key ? (
                  <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md px-2 py-1">
                    <span className="font-mono text-base truncate max-w-[180px] text-blue-800 dark:text-blue-200" title={item.pix_key}>
                      {item.pix_key}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 bg-blue-600 hover:bg-blue-700 text-white rounded"
                      onClick={() => {
                        navigator.clipboard.writeText(item.pix_key);
                        toast.success("Chave PIX copiada!");
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell className="max-w-[150px] truncate" title={item?.name}>{item?.name}</TableCell>
              <TableCell className="font-mono">
                {Mask.formatCurrency(item?.value)}
              </TableCell>
              <TableCell>
                {(() => {
                  const pix = getBtgPixLabel(
                    item?.btg_status,
                    item?.btg_needs_approval,
                    item?.devolution_status_id === DEVOLUTION_STATUS.PENDING &&
                      !!item?.btg_batch_id
                  );
                  const hasPixDetails =
                    !!item?.btg_receiver_name || !!item?.btg_batch_id || !!pix;
                  const canRefresh =
                    !!pix &&
                    item?.devolution_status_id === DEVOLUTION_STATUS.APPROVED &&
                    item?.btg_status !== "PROCESSED";

                  return (
                    <div className="flex items-center gap-1.5">
                      <Badge
                        className={cn(
                          "text-xs whitespace-nowrap",
                          pix
                            ? pix.badgeClassName
                            : getStatusColor(item?.devolution_status_id)
                        )}
                      >
                        {pix
                          ? `${item?.devolution_status?.name} · ${pix.short}`
                          : item?.devolution_status?.name}
                      </Badge>

                      {hasPixDetails && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              title="Detalhes do PIX"
                              className="text-muted-foreground hover:text-foreground"
                            >
                              {item?.btg_receiver_mismatch ? (
                                <AlertTriangle className="h-4 w-4 text-red-600" />
                              ) : (
                                <Info className="h-4 w-4" />
                              )}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-80 space-y-3 text-sm">
                            {pix && (
                              <p className={cn("font-semibold", pix.className)}>
                                {pix.text}
                              </p>
                            )}

                            {item?.btg_receiver_name ? (
                              <div
                                className={cn(
                                  "space-y-1 rounded-lg border border-l-4 p-3",
                                  item?.btg_receiver_mismatch
                                    ? "border-red-300 border-l-red-600 bg-red-50/70 dark:bg-red-950/20"
                                    : "border-blue-200 border-l-blue-600 bg-blue-50/60 dark:bg-blue-950/20"
                                )}
                              >
                                <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                  <UserCheck className="h-4 w-4" />
                                  Quem recebe (informado pelo BTG)
                                </p>
                                <p className="font-bold uppercase text-blue-800 dark:text-blue-200">
                                  {item.btg_receiver_name}
                                </p>
                                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
                                  <span className="text-muted-foreground">Documento</span>
                                  <span className="font-mono">{item.btg_receiver_tax_id}</span>
                                  <span className="text-muted-foreground">Banco</span>
                                  <span>{getBankName(item.btg_receiver_bank_code)}</span>
                                  <span className="text-muted-foreground">Chave</span>
                                  <span className="font-mono break-all">{item.pix_key}</span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                O BTG ainda não informou o titular da chave.
                              </p>
                            )}

                            {item?.btg_receiver_mismatch && (
                              <div className="flex items-start gap-1.5 rounded-md border border-red-400 bg-red-100 p-2 text-xs font-medium text-red-900 dark:bg-red-950/40 dark:text-red-200">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                                <span>
                                  O titular da chave é diferente do cliente da devolução.
                                </span>
                              </div>
                            )}

                            {item?.btg_end_to_end_id && (
                              <p className="font-mono text-[11px] break-all text-muted-foreground">
                                {item.btg_end_to_end_id}
                              </p>
                            )}
                          </PopoverContent>
                        </Popover>
                      )}

                      {canRefresh && (
                        <button
                          type="button"
                          title="Atualizar status do PIX"
                          disabled={syncingUuid === item.uuid}
                          onClick={() => handleSyncBtgStatus(item.uuid)}
                          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                        >
                          <RotateCcw
                            className={cn(
                              "h-4 w-4",
                              syncingUuid === item.uuid && "animate-spin"
                            )}
                          />
                        </button>
                      )}
                    </div>
                  );
                })()}
              </TableCell>
              <TableCell>{item?.created_by?.name}</TableCell>
              <TableCell>{formatDate(item?.created_at)}</TableCell>
              <TableCell className="flex justify-center">
                <div className="flex gap-3 w-full justify-center items-center">
                  <If
                    condition={
                      canManageReceipts ||
                      user?.user_type_id !== TypeUser.seller ||
                      item?.created_by?.id === user?.id
                    }
                  >
                    <ReceiptModal
                      devolution={item}
                      canManage={canManageReceipts}
                      onFilesCountChange={(count) => updateFilesCount(item.uuid, count)}
                    />
                  </If>
                  <If condition={true}>
                    <ShowBtn
                      labels={{
                        title: "Detalhes da Devolução",
                        description: "Informações completas sobre a devolução",
                      }}
                      params={{
                        func: getShowDevolution,
                        funcParams: item?.uuid,
                      }}
                      className="sm:max-w-[600px]"
                      render={(responseApi: TableDataProps) => (
                        <ScrollArea className="h-[500px] pr-4">
                          <div className="w-full h-auto space-y-6">
                            {/* Informações do Cliente */}
                            <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                              <h3 className="font-semibold text-lg mb-3 text-primary">
                                Informações do Cliente
                              </h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <span className="text-sm text-muted-foreground">
                                    Nome:
                                  </span>
                                  <p className="font-medium">
                                    {responseApi?.client?.name}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-muted-foreground">
                                    Email:
                                  </span>
                                  <p className="font-medium">
                                    {responseApi?.client?.email}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-muted-foreground">
                                    Telefone:
                                  </span>
                                  <p className="font-medium">
                                    {responseApi?.client?.phone ||
                                      "Não informado"}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-muted-foreground">
                                    Documento:
                                  </span>
                                  <p className="font-medium">
                                    {responseApi?.client?.document ||
                                      "Não informado"}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Detalhes da Devolução */}
                            <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                              <h3 className="font-semibold text-lg mb-3 text-primary">
                                Detalhes da Devolução
                              </h3>
                              <div className="space-y-3">
                                <div>
                                  <span className="text-sm text-muted-foreground">
                                    Descrição Completa:
                                  </span>
                                  <p className="font-medium mt-1 p-3 bg-white dark:bg-gray-700 rounded border">
                                    {responseApi?.name ||
                                      "Sem descrição disponível"}
                                  </p>
                                </div>

                                <div>
                                  <span className="text-sm text-muted-foreground">
                                    Observação (Após atualização):
                                  </span>
                                  <p className="font-medium mt-1 p-3 bg-white dark:bg-gray-700 rounded border">
                                    {responseApi?.observation ||
                                      "Não informado"}
                                  </p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <span className="text-sm text-muted-foreground">
                                      Valor:
                                    </span>
                                    <p className="font-semibold text-lg text-green-600 dark:text-green-400">
                                      {Mask.formatCurrency(responseApi?.value)}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-muted-foreground">
                                      Status:
                                    </span>
                                    <div className="mt-1">
                                      <Badge
                                        className={cn(
                                          "text-sm",
                                          getStatusColor(
                                            responseApi?.devolution_status_id
                                          )
                                        )}
                                      >
                                        {responseApi?.devolution_status?.name}
                                      </Badge>
                                    </div>
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <span className="text-sm text-muted-foreground">
                                    Chave PIX:
                                  </span>
                                  <div className="flex items-center gap-2 mt-1 p-3 bg-white dark:bg-gray-700 rounded border">
                                    <p className="font-mono text-sm break-all flex-1">
                                      {responseApi?.pix_key || "Não informado"}
                                    </p>
                                    {responseApi?.pix_key && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 px-2 shrink-0"
                                        onClick={() => {
                                          navigator.clipboard.writeText(responseApi.pix_key);
                                          toast.success("Chave PIX copiada!");
                                        }}
                                      >
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>

                                {responseApi?.btg_receiver_name && (
                                  <div
                                    className={cn(
                                      "mt-3 p-3 rounded border text-sm space-y-1",
                                      responseApi?.btg_receiver_mismatch
                                        ? "border-red-300 bg-red-50 dark:bg-red-950/30"
                                        : "bg-white dark:bg-gray-700"
                                    )}
                                  >
                                    <p className="text-xs text-muted-foreground">
                                      Quem recebe (informado pelo BTG)
                                    </p>
                                    <p className="font-semibold uppercase">
                                      {responseApi.btg_receiver_name}
                                    </p>
                                    <p className="font-mono text-xs">
                                      {responseApi.btg_receiver_tax_id}
                                    </p>
                                    <p className="text-xs">
                                      {getBankName(responseApi.btg_receiver_bank_code)}
                                      {responseApi.btg_key_type
                                        ? ` · chave ${getBtgKeyTypeLabel(responseApi.btg_key_type)}`
                                        : ""}
                                    </p>
                                    {responseApi.btg_end_to_end_id && (
                                      <p className="font-mono text-[11px] break-all text-muted-foreground">
                                        {responseApi.btg_end_to_end_id}
                                      </p>
                                    )}
                                    {responseApi.btg_receiver_mismatch && (
                                      <p className="text-xs font-medium text-red-700">
                                        O documento do titular da chave não bate com o do
                                        cliente. Confira antes de aprovar no BTG.
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Informações de Auditoria */}
                            <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                              <h3 className="font-semibold text-lg mb-3 text-primary">
                                Informações de Auditoria
                              </h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <span className="text-sm text-muted-foreground">
                                    Criado por:
                                  </span>
                                  <p className="font-medium">
                                    {responseApi?.created_by?.name}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-muted-foreground">
                                    Data de criação:
                                  </span>
                                  <p className="font-medium">
                                    {formatDate(responseApi?.created_at)}
                                  </p>
                                </div>
                                {responseApi?.updated_by && (
                                  <>
                                    <div>
                                      <span className="text-sm text-muted-foreground">
                                        Atualizado por:
                                      </span>
                                      <p className="font-medium">
                                        {responseApi?.updated_by?.name}
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-sm text-muted-foreground">
                                        Data de atualização:
                                      </span>
                                      <p className="font-medium">
                                        {formatDate(responseApi?.updated_at)}
                                      </p>
                                    </div>
                                  </>
                                )}
                              </div>

                              {/* Observações do Cliente (se houver) */}
                              {responseApi?.client?.observation && (
                                <div className="mt-3 pt-3 border-t">
                                  <span className="text-sm text-muted-foreground">
                                    Observações do Cliente:
                                  </span>
                                  <p className="font-medium mt-1 p-3 bg-white dark:bg-gray-700 rounded border">
                                    {responseApi?.client?.observation}
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* ID da Devolução */}
                            <div className="text-xs text-muted-foreground text-center">
                              ID: {responseApi?.uuid}
                            </div>
                          </div>
                        </ScrollArea>
                      )}
                    />
                  </If>
                  {item?.devolution_status_id === DEVOLUTION_STATUS.PENDING &&
                    user?.user_type_id !== TypeUser.seller && (
                      <>
                        <If condition={hasPermission("approve-devolution")}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              openStatusModal(item, DEVOLUTION_STATUS.APPROVED)
                            }
                            className="h-8 w-auto px-2 text-xs bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100"
                          >
                            Aprovar
                          </Button>
                        </If>
                        <If condition={hasPermission("reject-devolution")}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              openStatusModal(item, DEVOLUTION_STATUS.REJECTED)
                            }
                            className="h-8 w-auto px-2 text-xs bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
                          >
                            Rejeitar
                          </Button>
                        </If>
                      </>
                    )}
                  {user?.user_type_id !== TypeUser.seller && (
                    <If condition={hasPermission("delete-devolution")}>
                      <DeleteBtn
                        params={{
                          func: deleteDevolution,
                          funcParams: item?.uuid,
                        }}
                        updateFront={() =>
                          updateFront((prev) => ({
                            ...prev,
                            data: [
                              ...prev?.data?.filter(
                                (data) => data?.uuid !== item?.uuid
                              ),
                            ],
                          }))
                        }
                        referenceLabel={item?.name}
                      />
                    </If>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {dataApi?.data.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-8">
                Nenhuma devolução encontrada
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="relative flex flex-col items-center gap-2 mb-3 md:min-h-8">
        {dataApi?.data.length > 0 && (
          <span className="font-medium md:absolute md:left-0 md:top-1">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={(e) => {
                    e.preventDefault();
                    pageActions.previous();
                  }}
                />
              </PaginationItem>
              {getPageNumbers(dataApi?.current_page, dataApi?.last_page).map(
                (p, idx) => (
                  <PaginationItem key={`${p}-${idx}`}>
                    {p === "..." ? (
                      <PaginationEllipsis className="size-8" />
                    ) : (
                      <PaginationLink
                        href="#"
                        isActive={p === dataApi?.current_page}
                        className={cn(
                          "size-8 bg-transparent text-slate-900 dark:text-[#F4F4F5] hover:bg-stone-950/10",
                          p === dataApi?.current_page &&
                            "border-2 border-[#023859] font-bold cursor-auto"
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          pageActions.goToPage?.(p as number);
                        }}
                      >
                        {p}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                )
              )}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={(e) => {
                    e.preventDefault();
                    pageActions.next();
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      <AlertDialog
        open={statusModal.isOpen}
        onOpenChange={(open) =>
          setStatusModal({ isOpen: open, devolution: null, newStatusId: 0 })
        }
      >
        <AlertDialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          {statusModal.newStatusId === DEVOLUTION_STATUS.APPROVED && autoPix ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Você vai enviar</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-4 text-left break-words">
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
                      <p className="text-xs font-medium uppercase tracking-wide text-blue-700 dark:text-blue-300">
                        Valor a enviar
                      </p>
                      <p className="text-3xl font-bold text-blue-800 dark:text-blue-200">
                        {Mask.formatCurrency(statusModal.devolution?.value || 0)}
                      </p>
                      <p className="text-sm text-blue-900/70 dark:text-blue-200/70">
                        Conta vinculada ao sistema
                      </p>
                    </div>
                    {(() => {
                      const view = review.devolution ?? statusModal.devolution;

                      if (review.loading) {
                        return (
                          <div className="border-t pt-3 flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Consultando o titular da chave no BTG...
                          </div>
                        );
                      }

                      return (
                        <div
                          className={cn(
                            "space-y-2 rounded-lg border border-l-4 p-4",
                            view?.btg_receiver_mismatch
                              ? "border-red-300 border-l-red-600 bg-red-50/70 dark:bg-red-950/20"
                              : "border-blue-200 border-l-blue-600 bg-blue-50/60 dark:bg-blue-950/20"
                          )}
                        >
                          {view?.btg_receiver_name ? (
                            <>
                              <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                <UserCheck className="h-4 w-4" />
                                Quem vai receber (informado pelo BTG)
                              </p>
                              <p className="text-xl font-bold uppercase text-blue-800 dark:text-blue-200">
                                {view.btg_receiver_name}
                              </p>
                              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                                <span className="text-muted-foreground">Documento</span>
                                <span className="font-mono">{view.btg_receiver_tax_id}</span>
                                <span className="text-muted-foreground">Banco</span>
                                <span className="font-medium">
                                  {getBankName(view.btg_receiver_bank_code)}
                                </span>
                                <span className="text-muted-foreground">Chave</span>
                                <span className="font-mono break-all">
                                  {view?.pix_key}{" "}
                                  <span className="font-sans text-xs text-muted-foreground">
                                    ({getPixKeyType(view?.pix_key || "")})
                                  </span>
                                </span>
                              </div>
                            </>
                          ) : (
                            <p className="text-sm font-mono break-all">
                              {view?.pix_key}{" "}
                              <span className="text-xs text-muted-foreground">
                                ({getPixKeyType(view?.pix_key || "")})
                              </span>
                            </p>
                          )}
                          <div className="rounded bg-white/70 p-2 text-xs dark:bg-black/20">
                            <span className="text-muted-foreground">
                              Cliente da devolução:{" "}
                            </span>
                            <span className="font-medium uppercase">
                              {view?.client?.name}
                            </span>{" "}
                            · {formatDocument(view?.client?.document)}
                          </div>
                          {view?.btg_receiver_mismatch && (
                            <div className="rounded-md border border-red-400 bg-red-100 p-3 text-xs text-red-900 dark:bg-red-950/40 dark:text-red-200">
                              <p className="flex items-center gap-1.5 text-sm font-bold">
                                <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" />
                                O titular da chave é diferente do cliente.
                              </p>
                              <label className="mt-2 flex items-start gap-2">
                                <input
                                  type="checkbox"
                                  className="mt-0.5"
                                  checked={ackMismatch}
                                  onChange={(e) => setAckMismatch(e.target.checked)}
                                />
                                <span>
                                  Conferi e quero continuar mesmo assim.
                                </span>
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <div className="space-y-2 rounded-lg bg-muted/60 p-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Quando</span>
                        <span className="font-medium">Agora, sem repetir</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Via</span>
                        <span className="font-medium">Pix</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Mensagem</span>
                        <span className="font-medium text-right">
                          {statusModal.devolution?.name || "Não informado"}
                        </span>
                      </div>
                    </div>
                    <div className="border-t pt-3">
                      <label
                        htmlFor="observation"
                        className="block text-sm font-medium mb-1"
                      >
                        Observação
                      </label>
                      <Textarea
                        id="observation"
                        className="w-full rounded border border-gray-300 dark:border-gray-600 p-2 min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Digite uma observação (opcional)"
                        value={observation}
                        onChange={(e) => setObservation(e.target.value)}
                      />
                    </div>
                    <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                      <span>
                        O PIX já foi criado no BTG e só sai depois da aprovação do
                        representante legal no app do banco. Confirmar aprova a
                        devolução aqui; cancelar desfaz a criação no BTG.
                      </span>
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              {statusError && (
                <div
                  role="alert"
                  className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-200"
                >
                  <p className="font-medium">Não foi possível concluir</p>
                  <p className="mt-1">{statusError}</p>
                  <p className="mt-1 text-xs">
                    Nada foi enviado nem alterado. Você pode corrigir e tentar novamente.
                  </p>
                </div>
              )}
              <AlertDialogFooter className="flex-wrap gap-2 sm:space-x-0">
                <AlertDialogCancel
                  disabled={isSubmitting}
                  title="Mantém o PIX criado no BTG para você revisar depois"
                  className="mt-0"
                >
                  Fechar
                </AlertDialogCancel>
                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    isSubmitting || review.loading || !review.devolution?.btg_batch_id
                  }
                  onClick={handleCancelPix}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  Cancelar PIX
                </Button>
                <AlertDialogAction
                  disabled={
                    isSubmitting ||
                    review.loading ||
                    !review.devolution?.btg_receiver_name ||
                    (!!review.devolution?.btg_receiver_mismatch && !ackMismatch)
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    handleStatusChange();
                  }}
                  className="bg-primary hover:bg-primary/90"
                >
                  {isSubmitting ? "Aprovando..." : "Confirmar aprovação"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar alteração de status</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja{" "}
              {getStatusText(statusModal.newStatusId).toLowerCase()} esta
              devolução?
              <br />
              <br />
              <strong>Cliente:</strong> {statusModal.devolution?.client?.name}
              <br />
              <strong>Valor:</strong>{" "}
              {Mask.formatCurrency(statusModal.devolution?.value || 0)}
              <br />
              <strong>Descrição:</strong> {statusModal.devolution?.name}
              <br />
              <br />
              <div className="mt-2">
                <label
                  htmlFor="observation"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                >
                  Observação
                </label>
                <Textarea
                  id="observation"
                  className="w-full rounded border border-gray-300 dark:border-gray-600 p-2 min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Digite uma observação (opcional)"
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {statusError && (
            <div
              role="alert"
              className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-200"
            >
              <p className="font-medium">Não foi possível concluir</p>
              <p className="mt-1">{statusError}</p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={isSubmitting}
              onClick={(e) => {
                e.preventDefault();
                handleStatusChange();
              }}
              className="bg-primary hover:bg-primary/90"
            >
              {getStatusText(statusModal.newStatusId)}
            </AlertDialogAction>
          </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const getPageNumbers = (current: number, last: number): (number | "...")[] => {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
  const pages: (number | "...")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(last - 1, current + 1);
  if (start > 2) pages.push("...");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < last - 1) pages.push("...");
  pages.push(last);
  return pages;
};

export function DevolutionsList() {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const [sortConfig, setSortConfig] = useState<{
    column: string;
    direction: "asc" | "desc";
  }>({ column: "created_at", direction: "desc" });
  const [currentFilters, setCurrentFilters] = useState<DevolutionsFilters>({
    order_by: "created_at",
    order_direction: "desc",
    per_page: 50,
  });

  const fetchData = async (
    page: number = 1,
    filters: DevolutionsFilters = currentFilters
  ) => {
    setState("loading");
    const { data } = await getDevolutions(page, filters as any);
    setListData(data?.response);
    setState("production");
  };

  useEffect(() => {
    fetchData(1, currentFilters);
  }, []);

  const handleSort = async (column: string, direction: "asc" | "desc") => {
    setSortConfig({ column, direction });
    const newFilters = {
      ...currentFilters,
      order_by: column as DevolutionsFilters["order_by"],
      order_direction: direction,
    };
    setCurrentFilters(newFilters);
    await fetchData(1, newFilters);
  };

  const handleFiltersChange = (filters: DevolutionsFilters) => {
    setCurrentFilters(filters);
  };

  const handleApplyFilters = async () => {
    setSortConfig({
      column: currentFilters.order_by || "created_at",
      direction: currentFilters.order_direction || "desc",
    });
    await fetchData(1, currentFilters);
  };

  const handleClearFilters = async () => {
    const clearedFilters: DevolutionsFilters = {
      order_by: "created_at",
      order_direction: "desc",
      per_page: 50,
    };
    setCurrentFilters(clearedFilters);
    setSortConfig({ column: "created_at", direction: "desc" });
    await fetchData(1, clearedFilters);
  };

  const pageActions: TypePageActions & {
    refresh: () => Promise<void>;
    goToPage: (page: number) => Promise<void>;
  } = {
    refresh: async () => {
      await fetchData(listData?.current_page || 1, currentFilters);
    },
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        await fetchData(listData?.current_page + 1, currentFilters);
      }
      return;
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        await fetchData(listData?.current_page - 1, currentFilters);
      }
      return;
    },
    goToPage: async (page: number) => {
      if (page >= 1 && page <= listData?.last_page && page !== listData?.current_page) {
        await fetchData(page, currentFilters);
      }
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        await fetchData(listData?.last_page, currentFilters);
      }
      return;
    },
  };

  return (
    <Main>
      <PageHeader
        icon={RotateCcw}
        title="Devolucoes"
        description="Gerencie as devolucoes de produtos"
      >
        <AddDevolution
          updateFront={setListData}
          dataApi={listData}
          refreshList={pageActions.refresh}
        />
      </PageHeader>

      <DevolutionsFiltersComponent
        filters={currentFilters}
        onFiltersChange={handleFiltersChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />

      <div className="flex flex-col gap-3">
        {state === "loading" ? (
          <CreateLoaderTable cols={7} lines={6} />
        ) : (
          <TableData
            dataApi={listData}
            pageActions={pageActions}
            updateFront={setListData}
            onSort={handleSort}
            sortConfig={sortConfig}
          />
        )}
      </div>
    </Main>
  );
}
